import { useState } from 'react';
import { ImagePlus, Upload } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * Dashed drop-zone style asset upload. Stages a single image and hands the File
 * to `onUpload` (which uploads it and updates the parent's preview URL). Shared
 * by the reseller Branding page and the admin PlatformSettings Branding tab.
 */
export function UploadField({
  label,
  hint,
  value,
  onUpload,
}: {
  label: string;
  hint: string;
  value: string | null;
  onUpload: (file: File) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  async function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    setErr(false);
    try {
      await onUpload(file);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="block">
      <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">{label}</span>
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1.5 px-4 py-5 rounded-md border-2 border-dashed cursor-pointer transition',
          'border-zinc-300 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-600',
          'bg-zinc-50/50 dark:bg-zinc-900/40',
        )}
      >
        {value ? (
          <img src={value} alt={label} className="h-10 max-w-[140px] object-contain" />
        ) : (
          <span className="w-9 h-9 grid place-items-center rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400">
            <ImagePlus className="w-4 h-4" />
          </span>
        )}
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200 inline-flex items-center gap-1.5">
          <Upload className="w-3.5 h-3.5" />
          {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
        </span>
        <span className={cn('text-[11px]', err ? 'text-rose-600 dark:text-rose-400' : 'text-zinc-500 dark:text-zinc-400')}>
          {err ? 'Upload failed — check the file type and size.' : hint}
        </span>
        <input type="file" accept="image/*" className="sr-only" onChange={onChange} disabled={busy} />
      </div>
    </label>
  );
}
